import { create } from 'zustand';
import { supabase } from '@/lib/supabase/client';
import type { Experience } from '@/lib/types/supabase';

interface ExperienceState {
  experiences: Experience[];
  spotlights: any[];
  loading: boolean;
  error: string | null;
  
  // Actions
  loadExperiences: (force?: boolean) => Promise<void>;
  loadSpotlights: (force?: boolean) => Promise<void>;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
}

// Simple cache implementation
interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const CACHE_TTL = 15 * 60 * 1000; // 15 minutes
const cache: {
  experiences?: CacheEntry<Experience[]>;
  spotlights?: CacheEntry<any[]>;
} = {};

const isCacheValid = <T>(entry?: CacheEntry<T>) => {
  return entry && (Date.now() - entry.timestamp) < CACHE_TTL;
};

export const useExperienceStore = create<ExperienceState>((set, get) => ({
  experiences: [],
  spotlights: [],
  loading: false,
  error: null,

  loadExperiences: async (force = false) => {
    try {
      // Check cache first
      if (!force && isCacheValid(cache.experiences)) {
        set({ experiences: cache.experiences!.data, loading: false });
        return;
      }

      set({ loading: true, error: null });
      
      const { data, error } = await supabase
        .from('experiences')
        .select(`
          *,
          creator:creator_id (
            id,
            full_name,
            bio,
            profile_image,
            creator_profiles(*)
          ),
          category:categories(*),
          media:experience_media (
            url,
            type,
            order_index
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Process the data to unify creator_profile/creator_profiles naming
      const processedData = data?.map(exp => {
        if (exp.creator && Array.isArray(exp.creator.creator_profiles) && exp.creator.creator_profiles.length > 0) {
          return {
            ...exp,
            creator: {
              ...exp.creator,
              creator_profile: exp.creator.creator_profiles[0] || null
            }
          };
        }
        return exp;
      }) || [];

      // Update cache
      cache.experiences = {
        data: processedData,
        timestamp: Date.now()
      };

      set({ 
        experiences: processedData,
        loading: false,
        error: null
      });
    } catch (error) {
      console.error('Failed to load experiences:', error);
      set({ 
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load experiences',
        experiences: cache.experiences?.data || []
      });
    }
  },

  loadSpotlights: async (force = false) => {
    try {
      // Check cache first
      if (!force && isCacheValid(cache.spotlights)) {
        set({ spotlights: cache.spotlights!.data, loading: false });
        return;
      }

      set({ loading: true, error: null });
      
      const { data, error } = await supabase
        .from('creator_spotlights')
        .select(`
          *,
          creator:creator_id (
            id,
            full_name,
            profile_image,
            creator_profiles(*)
          )
        `)
        .eq('active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      
      // Process creator_profiles similar to other queries
      const processedData = data?.map(spotlight => {
        if (spotlight.creator && 
            Array.isArray(spotlight.creator.creator_profiles) && 
            spotlight.creator.creator_profiles.length > 0) {
          return {
            ...spotlight,
            creator: {
              ...spotlight.creator,
              creator_profile: spotlight.creator.creator_profiles[0] || null
            }
          };
        }
        return spotlight;
      }) || [];
      
      // Update cache
      cache.spotlights = {
        data: processedData,
        timestamp: Date.now()
      };

      set({ 
        spotlights: processedData,
        loading: false,
        error: null
      });
    } catch (error) {
      console.error('Failed to load spotlights:', error);
      set({ 
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load spotlights',
        spotlights: cache.spotlights?.data || []
      });
    }
  },

  clearError: () => set({ error: null }),
  setLoading: (loading: boolean) => set({ loading })
})); 